import { FeedbackThumb, type MessageFeedbackSubmission } from '@/chat-common/schemas/feedback-schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { selectAgentV2NextTurnWritingSampleFileId } from '@/agent-v2/chat-form/store/selectors'
import { RootState } from '@/store/store'
import { MultiSelect, MultiSelectOption } from '@/components/ui/MultiSelect'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { GlobalToastType, kToastDurationShortMs } from '@/constants/constants-ui'
import { selectFeedbackReasons } from '@/response-feedback/store/selectors'
import { useAppDispatch } from '@/store/store-hooks'

import { useEffect, useId, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'clsx'
import { Modal, ModalContent } from '@/components/modal/Modal'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'

type MessageFeedbackDialogProps = {
  conversationId: string
  positive: boolean
  visible: boolean
  onClose: () => void
  onSubmitted?: () => void
  onSubmit: (feedback: MessageFeedbackSubmission) => Promise<void>
}

/**
 * The MessageFeedbackDialog collects a thumbs up/down rating (plus optional reasons and free-text
 * details for a thumbs down) on an agent response.
 */
export default function MessageFeedbackDialog(props: MessageFeedbackDialogProps) {
  const { conversationId, positive, visible, onClose, onSubmit, onSubmitted } = props
  const dispatch = useAppDispatch()
  const textareaId = useId()

  const responseFeedbackTypes = useSelector((state: RootState) => selectFeedbackReasons(state))
  const negativeFeedbackTypes = responseFeedbackTypes.negative_reasons

  // Local state
  const [loading, setLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [text, setText] = useState<string>('')
  const [selectedNegativeReasons, setSelectedNegativeReasons] = useState<string[]>([])
  const writingSampleUsed = useSelector((state: RootState) => selectAgentV2NextTurnWritingSampleFileId(state, { conversationId }) !== null)
  const { trackEvent } = useAnalytics()

  // Allow submission if the form is valid, and there are actual changes to the form
  const canSubmit = positive || selectedNegativeReasons.length > 0

  // Ref for the textarea to enable autofocus
  const textareaRef = useRef<HTMLTextAreaElement | null>(null)
  const multiselectRef = useRef<HTMLButtonElement | null>(null)

  // Autofocus the textarea when the dialog becomes visible
  useEffect(() => {
    if (visible) {
      // Add a small delay to account for Radix dialog portal mounting and animations
      const timeoutId = setTimeout(() => {
        if (positive && textareaRef.current) {
          textareaRef.current.focus()
        } else if (!positive && multiselectRef.current) {
          multiselectRef.current.focus()
        }
      }, 0)

      return () => clearTimeout(timeoutId)
    }
  }, [visible, positive])

  // Handle submit
  async function handleSubmit() {
    if (!loading) {
      try {
        setError(null)
        setLoading(true)

        // Thumb
        const thumb: FeedbackThumb = positive ? FeedbackThumb.UP : FeedbackThumb.DOWN

        await onSubmit({ thumb, thumbText: text, thumbReasons: selectedNegativeReasons })

        if (onSubmitted) onSubmitted()
        else onClose()

        if (writingSampleUsed) {
          trackEvent(AnalyticsEvent.TemplateInfluencedFeedback, {
            conversationId,
            positive,
          })
        }

        dispatch(openGlobalToast({ type: GlobalToastType.SUCCESS, message: 'Thank you for the feedback', durationMs: kToastDurationShortMs }))
      } catch (error) {
        console.error(error)
        setError('There was an error submitting your feedback. Please try again.')
        dispatch(
          openGlobalToast({
            type: GlobalToastType.ERROR,
            message: 'There was an error submitting your feedback. Please try again.',
            durationMs: kToastDurationShortMs,
          })
        )
      } finally {
        setLoading(false)
      }
    }
  }

  // Convert negative feedback types to MultiSelect options
  const negativeFeedbackOptions: MultiSelectOption[] = Object.values(negativeFeedbackTypes).map((type) => ({
    value: type,
    label: type,
  }))

  return (
    <Modal open={visible} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        title={positive ? 'We appreciate your feedback' : 'What went wrong?'}
        width="md"
        height="max-md"
        preventDismiss
        footer={
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: onClose, disabled: loading }}
            primaryAction={{
              label: 'Submit',
              onClick: handleSubmit,
              loading,
              disabled: !canSubmit,
            }}
            inlineMessage={error ? { variant: 'error', children: error } : undefined}
          />
        }
      >
        <div className="space-y-4">
          {/* Multi-select Dropdown for negative feedback reasons */}
          {!positive && (
            <MultiSelect
              ref={multiselectRef}
              options={negativeFeedbackOptions}
              selected={selectedNegativeReasons}
              onChange={setSelectedNegativeReasons}
              placeholder="Select issue type"
            />
          )}

          <div className="relative flex min-h-[160px] flex-col rounded-[4px] border border-brand-neutral-400 bg-white focus-within:ring-2 focus-within:ring-inset focus-within:ring-brand-400">
            <label
              htmlFor={textareaId}
              className={clsx('text-brand-neutral-600 pointer-events-none', text.length === 0 ? 'absolute inset-x-3 top-3 text-base' : 'px-3 pt-2.5 text-xs')}
            >
              {positive ? 'What did you like about the response?' : 'What was the issue with the response? How could it be improved?'}
            </label>
            <textarea
              id={textareaId}
              disabled={loading}
              ref={textareaRef}
              className={clsx(
                'w-full flex-1 rounded-[4px] border-0 bg-transparent p-3 text-base resize-none focus:outline-hidden focus:ring-0 text-brand-neutral-950',
                text.length === 0 ? 'min-h-[158px]' : 'min-h-[128px] pt-1'
              )}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </div>
        </div>
      </ModalContent>
    </Modal>
  )
}
